import type {
  CompanyCreationV2JustificatifItem, CompanyCreationV2JustificatifSection,
  CompanyCreationV2JustificatifVerification, CompanyCreationV2Justificatifs,
} from '@api/company-creation-v2'
import { formatsLabel, type FileFormat } from '@components/ui-v2/FileField/logic'

// Contrôles de fichier (taille, formats, traduction des rejets react-dropzone) partagés avec
// `ui-v2/FileField` — déplacés dans le catalogue le 2026-09-21, ré-exportés ici pour
// `JustifRow.tsx` et les tests existants.
export {
  MAX_FILE_SIZE_BYTES, buildAccept, checkPdfFile, resolveDroppedFile,
} from '@components/ui-v2/FileField/logic'

// Ordre d'affichage des groupes de la page (identique à l'ordre renvoyé par le serveur).
const SECTION_ORDER: CompanyCreationV2JustificatifSection[] = [
  'identite', 'siege', 'activite', 'statuts',
]

const SECTION_LABELS: Record<CompanyCreationV2JustificatifSection, string> = {
  identite: 'Identité',
  siege: 'Siège social',
  activite: 'Activité',
  statuts: 'Statuts',
}

type JustificatifSectionGroup = {
  section: CompanyCreationV2JustificatifSection
  label: string
  items: CompanyCreationV2JustificatifItem[]
}

export const groupItemsBySection = (
  items: CompanyCreationV2JustificatifItem[],
): JustificatifSectionGroup[] => (
  SECTION_ORDER
    .map((section) => ({
      section,
      label: SECTION_LABELS[section],
      items: items.filter((item) => item.section === section),
    }))
    .filter((group) => group.items.length > 0)
)

const SUSPENDED_MESSAGE = 'Les pièces du dirigeant sont en cours de validation par notre service '
  + 'client, cette action n\'est pas possible pour le moment.'
export const DOCUMENT_TYPE_REQUIRED_MESSAGE = 'Choisissez d\'abord le type de pièce.'
export const PIECES_PENDING_MESSAGE = 'Vos pièces d\'identité et de domicile doivent être vérifiées '
  + 'avant de valider.'
// 502 `SALESFORCE_SYNC_FAILED` : le dossier (ou le fichier) est enregistré chez nous mais la
// synchro Salesforce exigée a échoué — le service client termine la formalité à la main (décision
// de Cédric du 2026-10-05). Partagé avec l'écran Dépôt de capital et la page Formalités.
export const FINALIZATION_FAILED_MESSAGE = 'La finalisation de votre formalité n\'a pas pu être '
  + 'réalisée. Merci de contacter notre service client à contact@ma-domiciliation.fr pour que '
  + 'nous puissions la terminer avec vous.'

// Codes d'erreur serveur (`{ error }`) traduits pour l'utilisateur.
const ERROR_MESSAGES: Record<string, string> = {
  SALESFORCE_SYNC_FAILED: FINALIZATION_FAILED_MESSAGE,
  'Invalid PDF files': 'Ce fichier n\'est pas un PDF valide.',
  INVALID_FILE_FORMAT: 'Ce fichier n\'est pas un PDF, un JPEG ou un PNG valide.',
  NETHEOS_SUSPENDED: SUSPENDED_MESSAGE,
  DOCUMENT_TYPE_REQUIRED: DOCUMENT_TYPE_REQUIRED_MESSAGE,
  NETHEOS_UNAVAILABLE: 'La vérification est momentanément indisponible, réessayez dans quelques instants.',
  NETHEOS_PIECE_VALIDATED: 'Cette pièce est déjà vérifiée et ne peut plus être remplacée.',
  NETHEOS_PIECES_PENDING: PIECES_PENDING_MESSAGE,
  BLOCKED_DUPLICATE: 'Votre dossier doit être vérifié par notre service conformité avant de pouvoir '
    + 'continuer. Nous revenons vers vous très rapidement.',
}

type ApiError = {
  response?: { status?: number, data?: { error?: string, missing?: string[] } }
}

const responseOf = (error: unknown) => (error as ApiError)?.response

// Message affiché sous la ligne quand le serveur refuse le fichier ou le type de pièce.
export const uploadErrorMessage = (error: unknown): string => {
  const response = responseOf(error)
  const code = response?.data?.error
  if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code]
  if (code && response?.status === 400) return code
  return 'L\'envoi a échoué, veuillez réessayer.'
}

// Liste des pièces manquantes renvoyée par la validation serveur (422), ou la raison du refus pour
// un dossier domicilié, sinon message générique.
export const validationErrorMessages = (error: unknown): string[] => {
  const response = responseOf(error)
  const missing = response?.data?.missing
  if (response?.status === 422 && Array.isArray(missing) && missing.length) return missing
  const code = response?.data?.error
  if (response?.status === 422 && code && ERROR_MESSAGES[code]) return [ERROR_MESSAGES[code]]
  return ['Une erreur est survenue, veuillez réessayer.']
}

export const isSuspended = (data: CompanyCreationV2Justificatifs | undefined | null): boolean => (
  !!data?.netheos.isSuspended
)

export const canValidateJustificatifs = (
  data: CompanyCreationV2Justificatifs | undefined | null,
): boolean => !!data && data.isComplete && data.netheos.canValidate

// Texte sous la ligne d'une pièce vérifiée par Netheos, `null` quand il n'y a rien à dire. Un refus
// ne donne ni motif ni nombre d'essais restants : seulement « illisible » (image sans texte
// exploitable, juste après le dépôt) ou « invalide ».
export const verificationMessage = (
  verification: CompanyCreationV2JustificatifVerification | null,
): string | null => {
  if (!verification) return null
  if (verification.status === 'rejected') {
    if (verification.reason === 'reopened') return 'Notre service client vous demande une autre pièce.'
    return verification.illegible
      ? 'Pièce illisible — veuillez déposer une image plus nette ou un autre fichier.'
      : 'Pièce invalide — veuillez déposer un autre document.'
  }
  if (verification.status === 'manual_review') return 'En cours de validation — aucune action requise.'
  if (verification.status === 'awaiting_pair') return 'Recto reçu — ajoutez le verso pour lancer la vérification.'
  if (verification.status === 'not_submitted') {
    return 'La vérification n\'a pas pu être lancée — déposez à nouveau la pièce.'
  }
  return null
}

export type RowState =
  | 'idle' | 'received' | 'uploading' | 'dragActive' | 'dragReject' | 'error'
  | 'validated' | 'rejected' | 'review' | 'awaitingPair'

type RowStateInputs = {
  isUploading: boolean
  isDragActive: boolean
  isDragReject: boolean
  hasError: boolean
  isReceived: boolean
  verification?: CompanyCreationV2JustificatifVerification | null
}

// État visuel d'une ligne : l'interaction en cours prime, puis l'état de vérification Netheos,
// puis la présence du fichier.
export const resolveRowState = ({
  isUploading, isDragActive, isDragReject, hasError, isReceived, verification = null,
}: RowStateInputs): RowState => {
  if (isUploading) return 'uploading'
  if (isDragReject) return 'dragReject'
  if (isDragActive) return 'dragActive'
  if (hasError) return 'error'
  if (verification?.status === 'manual_review') return 'review'
  if (verification?.status === 'validated') return 'validated'
  if (verification?.status === 'rejected' || verification?.status === 'not_submitted') return 'rejected'
  if (verification?.status === 'awaiting_pair') return 'awaitingPair'
  if (isReceived) return 'received'
  return 'idle'
}

// Une pièce validée n'est plus remplaçable ici, une pièce en vérification manuelle non plus.
export const isRowLocked = (
  verification: CompanyCreationV2JustificatifVerification | null,
): boolean => verification?.status === 'validated' || verification?.status === 'manual_review'

type CueText = { title: string, subtitle: string, titleClass: string }

const cue = (title: string, subtitle: string, titleClass = 'text-ink'): CueText => (
  { title, subtitle, titleClass }
)

export const buildCueTexts = (
  formats: FileFormat[],
  isVerified = false,
): Record<RowState, CueText> => {
  const label = formatsLabel(formats)
  const fileWord = formats.length > 1 ? 'fichier' : label
  const idle = cue(`Glissez-déposez votre ${fileWord} ici`, 'ou cliquez pour parcourir')
  return {
    idle,
    error: idle,
    received: cue('Reçu', 'Glissez ou cliquez pour remplacer', 'text-positive'),
    uploading: isVerified
      ? cue('Envoi et vérification en cours…', 'Quelques secondes', 'text-ui-gray-500')
      : cue('Envoi en cours…', 'Merci de patienter', 'text-ui-gray-500'),
    dragActive: cue('Déposez pour envoyer', `${label}, 20 Mo max`),
    dragReject: cue('Fichier non accepté', `${label} uniquement, 20 Mo max`, 'text-coral'),
    validated: cue('Vérifiée', 'Pièce validée', 'text-positive'),
    rejected: cue('Pièce refusée', 'Glissez ou cliquez pour en déposer une autre', 'text-coral'),
    review: cue('En cours de validation', 'Aucune action requise', 'text-ui-gray-500'),
    awaitingPair: cue('Recto reçu', 'Glissez ou cliquez pour remplacer'),
  }
}
